import { DocumentType, HumanProcessingStatus } from "@dune/types";
import { SubmissionWithParticipantsAndValidDecennialCertificate } from "@dune/types";
import { Button } from "@dune/ui";
import { FileStatusChip } from "../../../../component/common/files/FileStatusChip";

type RequiredDocumentsTypes = DocumentType;

interface ParticipantDocumentsReadOnlyProps {
	submissionParticipant: SubmissionWithParticipantsAndValidDecennialCertificate["participantToConstructionSiteSubmissions"][number];
	constructionSiteId: number;
	onDocumentClick?: (file: {
		id: string;
		key: string;
		bucket: string;
		mimeType: string;
		name: string;
	}) => void;
	loadingDocumentId?: string | null;
}

const getDocumentLabel = (documentType: RequiredDocumentsTypes): string => {
	const labels: Partial<Record<RequiredDocumentsTypes, string>> = {
		ATTESTATION_DECENNALE: "Attestation Décennale",
		CONVENTION_HONORAIRES: "Convention d'honoraires",
		DEVIS: "Devis",
		FACTURE: "Facture",
		RAPPORT_INITIAL_CONTROLE_TECHNIQUE: "Rapport",
		RAPPORT_ETUDE_TECHNIQUE: "Rapport",
		RAPPORT_GEOTECHNIQUE: "Rapport",
	};
	return labels[documentType] || documentType.replace(/_/g, " ").toLowerCase();
};

export const ParticipantDocumentsReadOnly = ({
	submissionParticipant,
	onDocumentClick,
	loadingDocumentId,
}: ParticipantDocumentsReadOnlyProps) => {
	const requiredDocuments: RequiredDocumentsTypes[] = [];
	const designerType = submissionParticipant.designerType;

	if (submissionParticipant.type === "DESIGNER") {
		requiredDocuments.push("CONVENTION_HONORAIRES", "ATTESTATION_DECENNALE");
		if (designerType === "GEOTECHNICIEN") {
			requiredDocuments.push("RAPPORT_GEOTECHNIQUE");
		}
		if (designerType === "BUREAU_ETUDE") {
			requiredDocuments.push("RAPPORT_ETUDE_TECHNIQUE");
		}
		if (designerType === "CONTROLEUR_TECHNIQUE") {
			requiredDocuments.push("RAPPORT_INITIAL_CONTROLE_TECHNIQUE");
		}
	} else if (submissionParticipant.type === "BUILDER") {
		requiredDocuments.push("DEVIS", "ATTESTATION_DECENNALE");
	} else if (submissionParticipant.type === "MATERIAL") {
		requiredDocuments.push("FACTURE");
	}

	const findFilesForType = (documentType: RequiredDocumentsTypes) => {
		const files: {
			id: string;
			key: string;
			bucket: string;
			mimeType: string;
			name: string;
			humanProcessingStatus: HumanProcessingStatus;
		}[] = submissionParticipant.participant.filesOnConstructionSiteSubmission
			.filter((file) => file.type === documentType)
			.map((file) => file.file);

		if (documentType === "ATTESTATION_DECENNALE") {
			files.push(
				...(submissionParticipant.decennialCertificatesOfSubmissionOrValidFromOthers?.map(
					(cert) => cert.file,
				) || []),
			);
		}
		return files;
	};

	return (
		<div className="flex flex-col gap-4">
			{requiredDocuments.map((documentType) => {
				const files = findFilesForType(documentType);
				return (
					<div key={documentType} className="flex flex-col gap-2">
						<p className="text-sm font-medium">
							{getDocumentLabel(documentType)}
						</p>
						<div className="flex flex-col gap-2">
							{files.length > 0 ? (
								files.map((file) => (
									<div
										key={file.id}
										className="flex gap-2 items-center justify-between"
									>
										<div className="flex items-center gap-2 flex-1">
											<Button
												variant="link"
												size="sm"
												className="text-blue-600 p-0 h-auto font-normal justify-start"
												onClick={() => onDocumentClick?.(file)}
												disabled={loadingDocumentId === file.id}
											>
												{file.name}
												{loadingDocumentId === file.id && " (Chargement...)"}
											</Button>
										</div>
										<FileStatusChip
											humanProcessingStatus={file.humanProcessingStatus}
										/>
									</div>
								))
							) : (
								<span className="text-gray-500 text-sm">Aucun document</span>
							)}
						</div>
					</div>
				);
			})}
		</div>
	);
};
